import jwtDecode from 'jwt-decode';

import type { Token } from '../user/checkIsAdminAllowedByToken';
import { LocalStorage } from '../user/interfaces';
import { User } from '../user/User';
import { getSharedAuthCookies, restoreCognitoDeviceKeyInLocalStorage } from './sharedAuthCookies';

type AdminIdTokenClaims = Token & {
  sub: string;
  'cognito:username'?: string;
};

/**
 * When admin localStorage is empty but Genie (or another DCM-SSO app) has already
 * set shared auth cookies, copy a validated admin session into localStorage so the
 * admin portal can boot without a second OAuth round-trip.
 */
export async function hydrateAdminAuthFromCookies(): Promise<boolean> {
  const { idToken: existingIdToken } = LocalStorage.getTokens();
  if (existingIdToken) return false;

  const { id_token, access_token, refresh_token, device_key, user_type } = getSharedAuthCookies();
  if (user_type !== 'admin') return false;
  if (!id_token || !access_token || !refresh_token) return false;
  if (!User.isIdTokenValid(id_token)) return false;

  const decodedToken = jwtDecode<AdminIdTokenClaims>(id_token);
  const isPublisherUser = await User.resolveIsPublisherUser(decodedToken);
  if (isPublisherUser !== true) return false;

  LocalStorage.setTokens({ id_token, access_token, refresh_token });

  if (device_key) {
    const username = decodedToken['cognito:username'] ?? decodedToken.sub;
    restoreCognitoDeviceKeyInLocalStorage('admin', username, device_key);
  }

  return true;
}
